import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Button,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import { ITipoAtividade, salvarTipoAtividade } from "services/modules/tarefas";

interface ModalTipoAtividadeProps {
  isOpen: boolean;
  onClose: () => void;
  refreshData: () => void;
  dataTipoAtividade?: ITipoAtividade;
}

export const ModalTipoAtividade: React.FC<ModalTipoAtividadeProps> = ({
  isOpen,
  onClose,
  refreshData,
  dataTipoAtividade,
}) => {
  const { openNotification } = useNavigation();
  const [loading, setLoading] = useState(false);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);

  const [errors, setErrors] = useState<{
    nome?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      if (dataTipoAtividade) {
        setNome(dataTipoAtividade.nome);
        setDescricao(dataTipoAtividade.descricao || "");
        setAtivo(dataTipoAtividade.ativo);
      } else {
        resetForm();
      }
    }
  }, [isOpen, dataTipoAtividade]);

  const resetForm = () => {
    setNome("");
    setDescricao("");
    setAtivo(true);
    setErrors({});
  };

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!nome.trim()) {
      newErrors.nome = "O Nome é obrigatório";
    } else if (nome.trim().length > 200) {
      newErrors.nome = "O Nome deve ter no máximo 200 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      openNotification({
        message: "Por favor, preencha todos os campos obrigatórios",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload: Partial<ITipoAtividade> = {
        idTipoAtividade: dataTipoAtividade?.idTipoAtividade || 0,
        nome: nome.trim(),
        descricao: descricao.trim() || undefined,
        ativo,
      };

      const result = await salvarTipoAtividade(payload);

      if (result) {
        openNotification({
          message: `Tipo de atividade ${dataTipoAtividade ? "atualizado" : "cadastrado"} com sucesso!`,
          type: "success",
        });

        handleClose();
        refreshData();
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao salvar tipo de atividade",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            {dataTipoAtividade
              ? "Editar tipo de atividade"
              : "Cadastrar tipo de atividade"}
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              error={!!errors.nome}
              helperText={errors.nome || "Obrigatório (máx. 200 caracteres)"}
              required
              inputProps={{ maxLength: 200 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              multiline
              rows={3}
              helperText="Opcional (máx. 500 caracteres)"
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                  color="primary"
                />
              }
              label="Ativo"
            />
          </Grid>

          <Grid item xs={12} mt={3}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                >
                  CANCELAR
                </Button>
              </Grid>
              <Grid item>
                <LoadingButton
                  variant="contained"
                  onClick={handleSave}
                  loading={loading}
                >
                  SALVAR
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
